import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowsUpDownIcon,
  Bars3Icon,
  CheckCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllFocusModes,
  getFocusMode,
  getFocusModeTailwindClasses,
} from '../constants/focusModes';
import { useOrganization } from '../context/OrganizationContext';
import { groupsService } from '../services/groups';
import { OrganizationGroup } from '../types/groups';
import { getAuthHeaders } from '../utils/auth';
import { formatCurrency } from '../utils/currency';
import { formatFocusMode } from '../utils/enumFormatting';
import { API_URL } from '../constants/urls';

// Types for prioritization functionality
interface Position {
  id: string;
  segment_id: string;
  segment_name: string;
  proposition_id: string;
  proposition_name: string;
  obvious_choice_score: number | null;
  som_value: number | null;
  sam_value: number | null;
  focus_mode: string | null;
  priority_order?: number;
  positioning_id?: string;
  market_penetration?: number;
}

type SortOption = 'best' | 'obvious' | 'som';

// Helper function to calculate positioning score when it's null in the database
function calculatePositioningScore(cell: any, organizationId?: string): number {
  // If we have a stored score, use it
  if (cell?.obvious_choice_score != null) {
    return cell.obvious_choice_score;
  }

  // Otherwise calculate from components (treat null as 0)
  const propScore = cell?.proposition_score_snapshot ?? 0;
  const segScore = cell?.segment_score_snapshot ?? 0;
  const relevanceMultiplier = cell?.relevance_multiplier ?? 1.0;

  const baseScore = (propScore + segScore) / 2;
  const calculatedScore = baseScore * relevanceMultiplier;

  // Return the calculated score without auto-saving
  // The backend should handle score calculation when positions are created/updated
  return calculatedScore;
}

// Sortable components for drag-and-drop
function SortablePropositionHeader({
  proposition,
  onGroupMenuOpen,
  reorderMode,
  canEdit,
}: {
  proposition: any;
  onGroupMenuOpen: any;
  reorderMode: boolean;
  canEdit: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: proposition.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const displayGroupName =
    proposition.group_name || proposition.group?.name || null;

  return (
    <th
      ref={setNodeRef}
      style={{ ...style, width: '160px', minWidth: '160px', maxWidth: '160px' }}
      className="px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-b border-gray-200"
    >
      <div className="flex flex-col items-center justify-center">
        {reorderMode && canEdit && (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab flex items-center mb-1"
          >
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 8h16M4 16h16"
              />
            </svg>
          </div>
        )}
        <div className="flex items-center justify-center gap-1">
          {proposition.group?.color && (
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: proposition.group.color }}
            />
          )}
          <div
            className="font-semibold text-center break-words leading-tight"
            style={{ maxWidth: '150px' }}
            title={proposition.name}
          >
            {proposition.name}
          </div>
        </div>
        {displayGroupName && (
          <div
            className="text-xs text-gray-400 font-normal mt-1 truncate max-w-[140px] mx-auto"
            title={displayGroupName}
          >
            ({displayGroupName})
          </div>
        )}
        {reorderMode && canEdit && (
          <div className="mt-1">
            <button
              onClick={(e) => onGroupMenuOpen(e, 'proposition', proposition)}
              className="p-1 text-gray-600 hover:text-gray-800"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zM12 13a1 1 0 110-2 1 1 0 010 2zM12 20a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </th>
  );
}

function SortableSegmentRow({
  segment,
  propositions,
  filteredMatrix,
  handleCellClick,
  onGroupMenuOpen,
  showDivider,
  currentGroup,
  reorderMode,
  organization,
  canEdit,
  heatmapView,
  matrix,
}: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: segment.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <>
      {showDivider && (
        <tr>
          <td
            colSpan={propositions.length + 1}
            className="py-2 px-6 bg-gray-100 border-t-2 border-blue-500"
          >
            <span className="text-sm font-bold text-blue-700">
              {segment.group_name || segment.group?.name || currentGroup}
            </span>
          </td>
        </tr>
      )}
      <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
        <td className="px-2 sm:px-4 py-4 min-w-[120px] w-[200px] max-w-[250px] align-middle border-r border-b border-gray-200">
          <div className="flex items-center">
            {reorderMode && canEdit && (
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab flex items-center mr-2"
              >
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 8h16M4 16h16"
                  />
                </svg>
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {segment.group?.color && (
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: segment.group.color }}
                  />
                )}
                <div
                  className="text-sm font-medium text-gray-900 break-words leading-tight line-clamp-2"
                  title={segment.name}
                >
                  {segment.name}
                </div>
              </div>
              {(segment.group_name || segment.group?.name) && (
                <div
                  className="text-xs text-gray-500 mt-1 truncate ml-4"
                  title={segment.group_name || segment.group?.name}
                >
                  ({segment.group_name || segment.group?.name})
                </div>
              )}
            </div>
            {reorderMode && canEdit && (
              <button
                onClick={(e) => onGroupMenuOpen(e, 'segment', segment)}
                className="p-1 text-gray-600 hover:text-gray-800"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zM12 13a1 1 0 110-2 1 1 0 010 2zM12 20a1 1 0 110-2 1 1 0 010 2z"
                  />
                </svg>
              </button>
            )}
          </div>
        </td>
        {propositions.map((p: any) => {
          const cell = filteredMatrix.find(
            (row: any) =>
              row.segment_id === segment.id && row.proposition_id === p.id,
          );

          return (
            <td
              key={p.id}
              className={`${heatmapView !== 'none' ? 'p-0 w-[160px] max-w-[160px] h-[160px] relative' : 'px-3 py-4 text-center w-[160px] max-w-[160px] border-r border-b border-gray-200'} ${
                cell && !reorderMode && heatmapView === 'none'
                  ? 'cursor-pointer hover:bg-gray-100'
                  : ''
              } align-middle`}
              onClick={() =>
                cell &&
                !reorderMode &&
                heatmapView === 'none' &&
                handleCellClick(cell.positioning_id || cell.id)
              }
            >
              {heatmapView !== 'none' ? (
                <HeatmapCell
                  cell={cell}
                  matrix={matrix}
                  organization={organization}
                  handleCellClick={handleCellClick}
                  mode={heatmapView as 'obviousness' | 'penetration'}
                />
              ) : (
                <>
                  {cell ? (
                    <div className="space-y-1">
                      {/* Score */}
                      <div
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getScoreChipColor(calculatePositioningScore(cell, organization?.id))}`}
                      >
                        Score:{' '}
                        {calculatePositioningScore(
                          cell,
                          organization?.id,
                        ).toFixed(1)}
                      </div>

                      {/* Focus Mode */}
                      <div
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getFocusModeTailwindClasses(cell.focus_mode, true)}`}
                      >
                        {formatFocusMode(cell.focus_mode) ?? 'No Focus'}
                      </div>

                      {/* SOM Value */}
                      {cell.som_value && (
                        <div className="inline-flex px-2 py-1 text-xs font-medium rounded-full border bg-blue-100 text-blue-800 border-blue-300">
                          SOM:{' '}
                          {formatCurrency(
                            Number(cell.som_value),
                            organization?.currency || 'USD',
                            {
                              compact: true,
                            },
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">No Data</span>
                  )}
                </>
              )}
            </td>
          );
        })}
      </tr>
    </>
  );
}

// Removed PositioningProps interface - using useOrganization hook instead

// Score chip colors that match the green intensity heatmap
const getScoreChipColor = (score: number) => {
  if (score >= 7) return 'bg-green-200 text-green-900 border-green-400';
  if (score >= 5) return 'bg-green-100 text-green-800 border-green-300';
  if (score >= 3) return 'bg-green-50 text-green-700 border-green-200';
  return 'bg-gray-100 text-gray-700 border-gray-300';
};

// Heatmap helper functions
const getHeatmapBackgroundColor = (score: number | null) => {
  // Treat N/A as 0.0 and use white background
  const actualScore = score || 0;

  // Clamp score to valid range
  const clampedScore = Math.max(0, Math.min(10, actualScore));

  // Use non-linear interpolation for more dramatic color changes at higher scores
  const _intensity = clampedScore / 10;

  // Create a gradient from white to vivid green with softer pastels in between
  if (clampedScore <= 3) {
    // 0-3: White to very light green (bg-green-50)
    const veryLightGreen = { r: 240, g: 253, b: 244 };
    const white = { r: 255, g: 255, b: 255 };
    const localIntensity = clampedScore / 3;

    const r = Math.round(
      white.r + (veryLightGreen.r - white.r) * localIntensity,
    );
    const g = Math.round(
      white.g + (veryLightGreen.g - white.g) * localIntensity,
    );
    const b = Math.round(
      white.b + (veryLightGreen.b - white.b) * localIntensity,
    );

    return `rgb(${r}, ${g}, ${b})`;
  } else if (clampedScore <= 7) {
    // 3-7: Light green to medium green (bg-green-100 to bg-green-300)
    const mediumGreen = { r: 134, g: 239, b: 172 };
    const lightGreen = { r: 240, g: 253, b: 244 };
    const localIntensity = (clampedScore - 3) / 4;

    const r = Math.round(
      lightGreen.r + (mediumGreen.r - lightGreen.r) * localIntensity,
    );
    const g = Math.round(
      lightGreen.g + (mediumGreen.g - lightGreen.g) * localIntensity,
    );
    const b = Math.round(
      lightGreen.b + (mediumGreen.b - lightGreen.b) * localIntensity,
    );

    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // 7-10: Medium green to vivid green (bg-green-300 to bg-green-500)
    const vividGreen = { r: 34, g: 197, b: 94 };
    const mediumGreen = { r: 134, g: 239, b: 172 };
    const localIntensity = (clampedScore - 7) / 3;

    const r = Math.round(
      mediumGreen.r + (vividGreen.r - mediumGreen.r) * localIntensity,
    );
    const g = Math.round(
      mediumGreen.g + (vividGreen.g - mediumGreen.g) * localIntensity,
    );
    const b = Math.round(
      mediumGreen.b + (vividGreen.b - mediumGreen.b) * localIntensity,
    );

    return `rgb(${r}, ${g}, ${b})`;
  }
};

const getHeatmapTextColor = (score: number | null) => {
  // Treat N/A as 0.0
  const actualScore = score || 0;
  // With softer pastel colors, we can use dark text throughout
  // Only use white text for very high scores where the background is darkest
  return actualScore >= 8 ? 'text-white' : 'text-gray-900';
};

// Penetration heatmap color function
const getPenetrationHeatmapBackgroundColor = (penetration: number) => {
  // Clamp penetration to valid range (0-100)
  const clampedPenetration = Math.max(0, Math.min(100, penetration));

  // Use non-linear interpolation for more dramatic color changes at higher penetration
  const _intensity = clampedPenetration / 100;

  // Create a gradient from white to vivid amber
  if (clampedPenetration <= 30) {
    // 0-30%: White to very light amber
    const veryLightAmber = { r: 254, g: 251, b: 235 }; // amber-50
    const white = { r: 255, g: 255, b: 255 };
    const localIntensity = clampedPenetration / 30;

    const r = Math.round(
      white.r + (veryLightAmber.r - white.r) * localIntensity,
    );
    const g = Math.round(
      white.g + (veryLightAmber.g - white.g) * localIntensity,
    );
    const b = Math.round(
      white.b + (veryLightAmber.b - white.b) * localIntensity,
    );

    return `rgb(${r}, ${g}, ${b})`;
  } else if (clampedPenetration <= 70) {
    // 30-70%: Light amber to medium amber
    const mediumAmber = { r: 251, g: 191, b: 36 }; // amber-400
    const lightAmber = { r: 254, g: 251, b: 235 }; // amber-50
    const localIntensity = (clampedPenetration - 30) / 40;

    const r = Math.round(
      lightAmber.r + (mediumAmber.r - lightAmber.r) * localIntensity,
    );
    const g = Math.round(
      lightAmber.g + (mediumAmber.g - lightAmber.g) * localIntensity,
    );
    const b = Math.round(
      lightAmber.b + (mediumAmber.b - lightAmber.b) * localIntensity,
    );

    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // 70-100%: Medium amber to vivid amber
    const vividAmber = { r: 217, g: 119, b: 6 }; // amber-600
    const mediumAmber = { r: 251, g: 191, b: 36 }; // amber-400
    const localIntensity = (clampedPenetration - 70) / 30;

    const r = Math.round(
      mediumAmber.r + (vividAmber.r - mediumAmber.r) * localIntensity,
    );
    const g = Math.round(
      mediumAmber.g + (vividAmber.g - mediumAmber.g) * localIntensity,
    );
    const b = Math.round(
      mediumAmber.b + (vividAmber.b - mediumAmber.b) * localIntensity,
    );

    return `rgb(${r}, ${g}, ${b})`;
  }
};

const getPenetrationTextColor = (penetration: number) => {
  // Use white text for darker amber backgrounds
  return penetration >= 60 ? 'text-white' : 'text-gray-900';
};

// Calculate market penetration from existing matrix data
const calculateMarketPenetration = (cell: any) => {
  if (!cell?.won_opportunities_count || !cell?.estimated_num_of_som_companies)
    return 0;

  const wonCustomers = cell.won_opportunities_count || 0;
  const somCompanies = cell.estimated_num_of_som_companies;

  return Math.min((wonCustomers / somCompanies) * 100, 100);
};

// Extract credibility data from existing matrix data
const getCredibilityData = (cell: any) => {
  return {
    testimonials: cell?.testimonials_count || 0,
    caseStudies: cell?.case_studies_count || 0,
    awards: cell?.awards_count || 0,
  };
};

// Enhanced heatmap cell component with market penetration and credibility
function HeatmapCell({
  cell,
  matrix,
  organization,
  handleCellClick,
  mode,
}: {
  cell: any;
  matrix: any[];
  organization: any;
  handleCellClick: (positionId: string) => void;
  mode: 'obviousness' | 'penetration';
}) {
  const score = calculatePositioningScore(cell, organization?.id);
  const somValue = cell?.som_value || 0;
  const penetration = calculateMarketPenetration(cell);
  const credibility = getCredibilityData(cell);

  // Calculate border thickness based on SOM value (bigger market = thicker border)
  const maxSom = Math.max(...matrix.map((c: any) => c.som_value || 0));
  const borderThickness = maxSom > 0 ? Math.max(1, (somValue / maxSom) * 6) : 1;

  if (!cell) {
    return (
      <div className="h-full w-full bg-gray-50 flex items-center justify-center">
        <span className="text-sm text-gray-400">No Data</span>
      </div>
    );
  }

  const backgroundColor =
    mode === 'obviousness'
      ? getHeatmapBackgroundColor(score)
      : getPenetrationHeatmapBackgroundColor(penetration);

  const textColorClass =
    mode === 'obviousness'
      ? getHeatmapTextColor(score)
      : getPenetrationTextColor(penetration);

  return (
    <div
      className={`h-full w-full p-3 cursor-pointer relative ${textColorClass} transition-all duration-200 hover:scale-105 hover:z-10 flex flex-col justify-center items-center`}
      style={{
        backgroundColor,
        borderWidth: `${borderThickness}px`,
        borderColor: '#374151',
        borderStyle: 'solid',
      }}
      onClick={() => handleCellClick(cell.positioning_id || cell.id)}
    >
      {/* Score prominently displayed */}
      <div className="text-center mb-2">
        <div className="text-2xl font-bold">{(score || 0).toFixed(1)}</div>
        <div className="text-xs opacity-90 font-medium">Score</div>
      </div>

      {/* Market penetration progress bar */}
      <div className="mb-2">
        <div className="bg-black/30 rounded-full h-2 mb-1">
          <div
            className="bg-white/90 h-2 rounded-full transition-all duration-300"
            style={{ width: `${penetration}%` }}
          />
        </div>
        <div className="text-xs text-center opacity-90">
          {penetration > 0
            ? `${penetration.toFixed(0)}% captured`
            : 'No penetration'}
        </div>
      </div>

      {/* SOM value */}
      {somValue > 0 && (
        <div className="text-center mb-2">
          <div className="text-sm font-semibold opacity-90">
            {formatCurrency(somValue, organization?.currency || 'USD', {
              compact: true,
            })}
          </div>
          <div className="text-xs opacity-75">SOM</div>
        </div>
      )}

      {/* Credibility badges */}
      <div className="absolute bottom-2 right-2 flex gap-1">
        {credibility.awards > 0 && (
          <div className="bg-amber-200 text-amber-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shadow-sm">
            {credibility.awards}
          </div>
        )}
        {credibility.caseStudies > 0 && (
          <div className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shadow-sm">
            {credibility.caseStudies}
          </div>
        )}
        {credibility.testimonials > 0 && (
          <div className="bg-emerald-200 text-emerald-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shadow-sm">
            {credibility.testimonials}
          </div>
        )}
      </div>
    </div>
  );
}

// Prioritization helper functions
const calculateCredibilityScore = (position: Position): number => {
  return position.obvious_choice_score || 0;
};

const calculateMarketOpportunity = (position: Position): number => {
  const somValue = position.som_value || 0;
  const penetration = position.market_penetration || 0;

  // S-curve: opportunity decreases as penetration increases
  const opportunityMultiplier = 1 / (1 + Math.exp(10 * (penetration - 0.5)));

  return somValue * opportunityMultiplier;
};

const calculateBestScore = (
  position: Position,
  allPositions: Position[],
): number => {
  const credibility = calculateCredibilityScore(position);
  const opportunity = calculateMarketOpportunity(position);
  const somValue = position.som_value || 0;

  // Normalize values and combine
  const maxSom = Math.max(...allPositions.map((p) => p.som_value || 0));
  const normalizedCredibility = credibility / 10;
  const normalizedOpportunity = maxSom > 0 ? opportunity / maxSom : 0;
  const normalizedValue = maxSom > 0 ? somValue / maxSom : 0;

  // Weighted combination: 40% credibility, 40% opportunity, 20% absolute value
  return (
    normalizedCredibility * 0.4 +
    normalizedOpportunity * 0.4 +
    normalizedValue * 0.2
  );
};

// Sortable Position Item Component for prioritization view
function SortablePositionItem({
  position,
  onFocusModeChange,
  currency,
  canEdit,
  isSelected,
  onClick,
}: {
  position: Position;
  onFocusModeChange: (id: string, mode: string) => void;
  currency: string;
  canEdit: boolean;
  isSelected: boolean;
  onClick: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: position.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const _focusMode = getFocusMode(position.focus_mode);

  const getScoreColor = (score: number | null) => {
    if (!score) return '';
    if (score >= 7) return 'bg-green-100 text-green-700';
    if (score >= 5) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg border-2 ${
        isDragging
          ? 'border-blue-500 shadow-lg'
          : isSelected
            ? 'border-blue-500'
            : 'border-gray-200 hover:border-gray-300'
      } p-4 cursor-pointer transition-all`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-gray-900 truncate">
            {position.segment_name} × {position.proposition_name}
          </h3>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            {position.obvious_choice_score !== null && (
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(
                  position.obvious_choice_score,
                )}`}
              >
                {position.obvious_choice_score?.toFixed(1)}/10
              </span>
            )}

            {position.som_value && (
              <span className="text-xs text-gray-600">
                SOM:{' '}
                {formatCurrency(position.som_value, currency, {
                  compact: true,
                })}
              </span>
            )}

            {position.sam_value && (
              <span className="text-xs text-gray-600">
                SAM:{' '}
                {formatCurrency(position.sam_value, currency, {
                  compact: true,
                })}
              </span>
            )}

            {position.market_penetration !== undefined && (
              <span className="text-xs text-gray-600">
                Penetration: {(position.market_penetration * 100).toFixed(1)}%
              </span>
            )}
          </div>

          <div className="mt-2">
            {canEdit ? (
              <select
                value={position.focus_mode || 'None'}
                onChange={(e) => {
                  e.stopPropagation();
                  onFocusModeChange(position.id, e.target.value);
                }}
                className="text-xs border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                onClick={(e) => e.stopPropagation()}
              >
                {getAllFocusModes().map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            ) : (
              <span
                className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getFocusModeTailwindClasses(
                  position.focus_mode,
                  true,
                )}`}
              >
                {formatFocusMode(position.focus_mode) || 'No Focus'}
              </span>
            )}
          </div>
        </div>

        {canEdit && (
          <div
            {...attributes}
            {...listeners}
            className="ml-2 cursor-grab text-gray-400 hover:text-gray-600"
          >
            <Bars3Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function PositioningTailwind() {
  const navigate = useNavigate();
  const { selectedOrg: organization, canEdit } = useOrganization();
  const [segments, setSegments] = useState<any[]>([]);
  const [propositions, setPropositions] = useState<any[]>([]);
  const [matrix, setMatrix] = useState<any[]>([]);
  // Load saved display settings from sessionStorage
  const [hideNotRelevant, setHideNotRelevant] = useState(() => {
    const saved = sessionStorage.getItem('positioning_hideNotRelevant');
    return saved === null ? true : saved === 'true';
  });
  const [heatmapMode, setHeatmapMode] = useState<
    'none' | 'obviousness' | 'penetration' | 'prioritization'
  >(() => {
    const saved = sessionStorage.getItem('positioning_heatmapMode');
    return (
      (saved as 'none' | 'obviousness' | 'penetration' | 'prioritization') ||
      'none'
    );
  });

  // Drag and drop state
  const [reorderMode, setReorderMode] = useState(false);
  const [_activeId, setActiveId] = useState<string | null>(null);
  const [dragType, setDragType] = useState<'segment' | 'proposition' | null>(
    null,
  );

  // Group management state
  const [groupMenu, setGroupMenu] = useState<{
    anchorEl: null | HTMLElement;
    type: 'segment' | 'proposition' | null;
    item: any;
  }>({ anchorEl: null, type: null, item: null });
  const [_groupName, setGroupName] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [groups, setGroups] = useState<OrganizationGroup[]>([]);
  const [legendExpanded, setLegendExpanded] = useState(false);

  // Prioritization state
  const [positions, setPositions] = useState<Position[]>([]);
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(
    null,
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const fetchData = () => {
    if (!organization) {
      // console.error removed
      return;
    }

    axios
      .get(`${API_URL}/segments?organization_id=${organization.id}`)
      .then((res: any) => setSegments(res.data))
      .catch((_err: any) => {
        // Error fetching segments
      });

    axios
      .get(`${API_URL}/propositions?organization_id=${organization.id}`)
      .then((res: any) => setPropositions(res.data))
      .catch((_err: any) => {
        // Error fetching propositions
      });

    axios
      .get(`${API_URL}/positions/matrix?organization_id=${organization.id}`, {
        headers: getAuthHeaders(),
      })
      .then((res: any) => {
        // New endpoint returns { propositions, segments, matrix }
        // Extract just the matrix array for compatibility
        setMatrix(res.data.matrix || res.data);
      })
      .catch((_err: any) => {
        // Error fetching matrix
      });

    // Note: Business metrics data is available in the matrix data, no separate API call needed
  };

  // Transform matrix data to positions for prioritization view
  const transformMatrixToPositions = (): Position[] => {
    const filteredMatrix = matrix.filter((item) =>
      hideNotRelevant ? item.focus_mode !== 'Not Relevant' : true,
    );

    const transformedPositions = filteredMatrix.map((item, index) => ({
      id: `${item.segment_id}-${item.proposition_id}`,
      segment_id: item.segment_id,
      segment_name: item.segment_name,
      proposition_id: item.proposition_id,
      proposition_name: item.proposition_name,
      obvious_choice_score: item.obvious_choice_score,
      som_value: item.som_value,
      sam_value: item.sam_value,
      focus_mode: item.focus_mode,
      priority_order: item.priority_order ?? index, // Use ?? to handle 0 values
      positioning_id: item.positioning_id,
      market_penetration: item.market_penetration || 0,
    }));

    // Sort by priority_order if positions have them, otherwise maintain current order
    return transformedPositions.sort((a, b) => {
      // If both have priority_order defined, use that
      if (a.priority_order !== undefined && b.priority_order !== undefined) {
        return a.priority_order - b.priority_order;
      }
      // If neither has priority_order, maintain current relative position
      return 0;
    });
  };

  // Sort positions by different algorithms
  const sortPositions = (
    positionsToSort: Position[],
    option: SortOption,
  ): Position[] => {
    const sorted = [...positionsToSort].sort((a, b) => {
      switch (option) {
        case 'best': {
          return (
            calculateBestScore(b, positionsToSort) -
            calculateBestScore(a, positionsToSort)
          );
        }
        case 'obvious': {
          const aScore = a.obvious_choice_score || 0;
          const bScore = b.obvious_choice_score || 0;
          return bScore - aScore;
        }
        case 'som': {
          const aSom = a.som_value || 0;
          const bSom = b.som_value || 0;
          return bSom - aSom;
        }
        default:
          return 0;
      }
    });

    return sorted.map((item, index) => ({
      ...item,
      priority_order: index,
    }));
  };

  // Handle auto-sort
  const handleAutoSort = (option: SortOption) => {
    const sortedPositions = sortPositions(positions, option);
    setPositions(sortedPositions);
    setHasUnsavedChanges(true);
  };

  // Handle drag end for prioritization
  const handlePrioritizationDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setPositions((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);
        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update priority_order based on new positions
        const updatedItems = newItems.map((item, index) => ({
          ...item,
          priority_order: index,
        }));

        setHasUnsavedChanges(true);
        return updatedItems;
      });
    }
  };

  // Handle focus mode change
  const handleFocusModeChange = (positionId: string, mode: string) => {
    setPositions((prev) =>
      prev.map((pos) =>
        pos.id === positionId ? { ...pos, focus_mode: mode } : pos,
      ),
    );
    setHasUnsavedChanges(true);
  };

  // Save prioritization changes
  const saveChanges = async () => {
    if (!organization || !hasUnsavedChanges) return;

    try {
      // Update each position individually with the new API
      const updatePromises = positions.map((pos, index) =>
        axios.put(
          `${API_URL}/positions/${pos.positioning_id}?organization_id=${organization.id}`,
          {
            priority_order: index, // Use index to ensure correct order
            focus_mode: pos.focus_mode || 'None',
          },
          {
            headers: getAuthHeaders(),
          },
        ),
      );

      await Promise.all(updatePromises);

      setHasUnsavedChanges(false);

      // Update positions with saved priority_order to maintain state
      const updatedPositions = positions.map((pos, index) => ({
        ...pos,
        priority_order: index,
      }));
      setPositions(updatedPositions);

      // Refresh the matrix data to sync changes (but don't reset positions immediately)
      fetchData();
    } catch (error) {
      // console.error removed
      alert('Error saving position priorities. Please try again.');
    }
  };

  const fetchGroups = async () => {
    if (!organization) return;

    try {
      const [segmentGroups, propositionGroups] = await Promise.all([
        groupsService.getGroups(organization.id, 'segment'),
        groupsService.getGroups(organization.id, 'proposition'),
      ]);

      // Combine, dedupe, and sort by display_order with Ungrouped last
      const allGroups = [...segmentGroups, ...propositionGroups];
      const uniqueGroups = allGroups
        .filter(
          (group, index, self) =>
            index === self.findIndex((g) => g.id === group.id),
        )
        .sort((a, b) => {
          const aUngrouped = a.name === 'Ungrouped';
          const bUngrouped = b.name === 'Ungrouped';
          if (aUngrouped && !bUngrouped) return 1;
          if (bUngrouped && !aUngrouped) return -1;
          return (
            (a.display_order ?? Number.MAX_SAFE_INTEGER) -
            (b.display_order ?? Number.MAX_SAFE_INTEGER)
          );
        });

      setGroups(uniqueGroups);
    } catch (error) {
      // console.error removed
    }
  };

  useEffect(() => {
    if (organization) {
      fetchData();
      fetchGroups();
    }
  }, [organization]);

  // Refresh data when page becomes visible again (e.g., after navigating back)
  useEffect(() => {
    const handleFocus = () => {
      if (organization) {
        fetchData(); // Refresh matrix data when page gains focus
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && organization) {
        fetchData(); // Refresh when tab becomes visible
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [organization]);

  // Save display settings to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(
      'positioning_hideNotRelevant',
      hideNotRelevant.toString(),
    );
  }, [hideNotRelevant]);

  useEffect(() => {
    sessionStorage.setItem('positioning_heatmapMode', heatmapMode);
  }, [heatmapMode]);

  // Update positions when matrix changes or switching to prioritization mode
  useEffect(() => {
    if (heatmapMode === 'prioritization' && matrix.length > 0) {
      // Only update if we don't have positions yet, or if the matrix actually changed
      if (positions.length === 0) {
        setPositions(transformMatrixToPositions());
      }
    }
  }, [heatmapMode, matrix, hideNotRelevant]);

  // Update positions when matrix changes (but preserve order if we have unsaved changes)
  useEffect(() => {
    if (
      heatmapMode === 'prioritization' &&
      matrix.length > 0 &&
      !hasUnsavedChanges
    ) {
      setPositions(transformMatrixToPositions());
    }
  }, [matrix]);

  const filteredMatrix = hideNotRelevant
    ? matrix.filter((row: any) => row.focus_mode !== 'Not Relevant')
    : matrix;

  // Group helpers (names from either name field or embedded group object)
  const getPropGroupName = (p: any) =>
    p?.group_name || p?.group?.name || 'Ungrouped';
  const getSegGroupName = (s: any) =>
    s?.group_name || s?.group?.name || 'Ungrouped';

  // Grouped, Ungrouped-last ordering used for rendering and DnD constraints
  const groupedPropositions = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const p of propositions) {
      const g = getPropGroupName(p);
      if (!map[g]) map[g] = [];
      map[g].push(p);
    }
    // Order groups by display_order using fetched groups list; Ungrouped last
    const orderIndex = new Map<string, number>();
    groups.forEach((g, i) =>
      orderIndex.set(
        g.name,
        g.name === 'Ungrouped'
          ? Number.MAX_SAFE_INTEGER
          : (g.display_order ?? i),
      ),
    );
    const entries = Object.entries(map).sort(([a], [b]) => {
      const aOrder = orderIndex.has(a)
        ? (orderIndex.get(a) as number)
        : Number.MAX_SAFE_INTEGER - 1;
      const bOrder = orderIndex.has(b)
        ? (orderIndex.get(b) as number)
        : Number.MAX_SAFE_INTEGER - 1;
      return aOrder - bOrder;
    });
    return entries;
  }, [propositions, groups]);

  const groupedSegments = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const s of segments) {
      const g = getSegGroupName(s);
      if (!map[g]) map[g] = [];
      map[g].push(s);
    }
    const orderIndex = new Map<string, number>();
    groups.forEach((g, i) =>
      orderIndex.set(
        g.name,
        g.name === 'Ungrouped'
          ? Number.MAX_SAFE_INTEGER
          : (g.display_order ?? i),
      ),
    );
    const entries = Object.entries(map).sort(([a], [b]) => {
      const aOrder = orderIndex.has(a)
        ? (orderIndex.get(a) as number)
        : Number.MAX_SAFE_INTEGER - 1;
      const bOrder = orderIndex.has(b)
        ? (orderIndex.get(b) as number)
        : Number.MAX_SAFE_INTEGER - 1;
      return aOrder - bOrder;
    });
    return entries;
  }, [segments, groups]);

  // Flattened arrays with Ungrouped last for non-reorder rendering
  const flatPropositions = useMemo(
    () => groupedPropositions.flatMap(([, props]) => props),
    [groupedPropositions],
  );
  const flatSegments = useMemo(
    () => groupedSegments.flatMap(([, segs]) => segs),
    [groupedSegments],
  );

  const handleCellClick = (positionId: string) => {
    navigate(`/positions/${positionId}`);
  };

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);

    // Determine if we're dragging a segment or proposition
    if (segments.find((s) => s.id === active.id)) {
      setDragType('segment');
    } else if (propositions.find((p) => p.id === active.id)) {
      setDragType('proposition');
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      if (dragType === 'segment') {
        // Reorder within the visible group only
        setSegments((prev) => {
          const activeItem = prev.find((s) => s.id === active.id);
          const overItem = prev.find((s) => s.id === over?.id);
          if (!activeItem || !overItem) return prev;
          const groupName = getSegGroupName(activeItem);
          if (groupName !== getSegGroupName(overItem)) return prev; // block cross-group

          const visibleGroup = prev.filter(
            (s) => getSegGroupName(s) === groupName,
          );
          const oldIndex = visibleGroup.findIndex((s) => s.id === active.id);
          const newIndex = visibleGroup.findIndex((s) => s.id === over?.id);
          const reordered = arrayMove(visibleGroup, oldIndex, newIndex).map(
            (s, idx) => ({ ...s, display_order: idx }),
          );
          // Rebuild full list preserving original relative order of other groups
          const result: any[] = [];
          let gi = 0;
          for (const s of prev) {
            if (getSegGroupName(s) === groupName) {
              result.push(reordered[gi++]);
            } else {
              result.push(s);
            }
          }
          return result;
        });
      } else if (dragType === 'proposition') {
        setPropositions((prev) => {
          const activeItem = prev.find((p) => p.id === active.id);
          const overItem = prev.find((p) => p.id === over?.id);
          if (!activeItem || !overItem) return prev;
          const groupName = getPropGroupName(activeItem);
          if (groupName !== getPropGroupName(overItem)) return prev; // block cross-group

          const visibleGroup = prev.filter(
            (p) => getPropGroupName(p) === groupName,
          );
          const oldIndex = visibleGroup.findIndex((p) => p.id === active.id);
          const newIndex = visibleGroup.findIndex((p) => p.id === over?.id);
          const reordered = arrayMove(visibleGroup, oldIndex, newIndex).map(
            (p, idx) => ({ ...p, display_order: idx }),
          );
          const result: any[] = [];
          let gi = 0;
          for (const p of prev) {
            if (getPropGroupName(p) === groupName) {
              result.push(reordered[gi++]);
            } else {
              result.push(p);
            }
          }
          return result;
        });
      }
    }

    setActiveId(null);
    setDragType(null);
  };

  const handleSaveOrder = async () => {
    if (!organization?.id) return;

    try {
      // Save segment order
      const segmentOrders = segments.map((seg, index) => ({
        id: seg.id,
        display_order: index,
        group_name: seg.group_name || null,
        group_id: seg.group_id || null,
      }));

      await axios.put(
        `${API_URL}/segments/update-order?organization_id=${organization.id}`,
        {
          items: segmentOrders,
        },
        {
          headers: getAuthHeaders(),
        },
      );

      // Save proposition order
      const propositionOrders = propositions.map((prop, index) => ({
        id: prop.id,
        display_order: index,
        group_name: prop.group_name || null,
        group_id: prop.group_id || null,
      }));

      await axios.put(
        `${API_URL}/propositions/update-order?organization_id=${organization.id}`,
        {
          items: propositionOrders,
        },
        {
          headers: getAuthHeaders(),
        },
      );

      setReorderMode(false);
      // Refetch data to ensure consistency
      fetchData();
    } catch (error: any) {
      // console.error removed
      if (error.response?.data) {
        // console.error removed
      }
    }
  };

  const handleGroupMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    type: 'segment' | 'proposition',
    item: any,
  ) => {
    setGroupMenu({ anchorEl: event.currentTarget, type, item });
    setGroupName(item.group_name || '');
    setSelectedGroupId(item.group_id || null);
  };

  const handleGroupMenuClose = () => {
    setGroupMenu({ anchorEl: null, type: null, item: null });
    setGroupName('');
    setSelectedGroupId(null);
  };

  const handleGroupSave = () => {
    if (!groupMenu.item) return;

    const selectedGroup = groups.find((g) => g.id === selectedGroupId);

    if (groupMenu.type === 'segment') {
      setSegments(
        segments.map((seg) =>
          seg.id === groupMenu.item.id
            ? {
                ...seg,
                group_id: selectedGroupId,
                group_name: selectedGroup?.name || null,
                group: selectedGroup
                  ? {
                      id: selectedGroup.id,
                      name: selectedGroup.name,
                      color: selectedGroup.color,
                    }
                  : null,
              }
            : seg,
        ),
      );
    } else {
      setPropositions(
        propositions.map((prop) =>
          prop.id === groupMenu.item.id
            ? {
                ...prop,
                group_id: selectedGroupId,
                group_name: selectedGroup?.name || null,
                group: selectedGroup
                  ? {
                      id: selectedGroup.id,
                      name: selectedGroup.name,
                      color: selectedGroup.color,
                    }
                  : null,
              }
            : prop,
        ),
      );
    }

    handleGroupMenuClose();
  };

  if (!organization) {
    return (
      <div className="p-6">
        <p className="text-lg text-gray-600">
          Select an organization to view the positioning matrix.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold">Positioning Matrix</h1>
        <div className="flex flex-wrap items-center gap-2">
          {reorderMode ? (
            <>
              <button
                onClick={handleSaveOrder}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Save Order
              </button>
              <button
                onClick={() => {
                  setReorderMode(false);
                  fetchData(); // Reset to original order
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Cancel
              </button>
            </>
          ) : (
            <>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={heatmapMode === 'obviousness'}
                  onChange={(e) =>
                    setHeatmapMode(e.target.checked ? 'obviousness' : 'none')
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Obviousness Heatmap</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={heatmapMode === 'penetration'}
                  onChange={(e) =>
                    setHeatmapMode(e.target.checked ? 'penetration' : 'none')
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Penetration Heatmap</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={heatmapMode === 'prioritization'}
                  onChange={(e) =>
                    setHeatmapMode(e.target.checked ? 'prioritization' : 'none')
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Prioritization View</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={hideNotRelevant}
                  onChange={(e) => setHideNotRelevant(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Hide Not Relevant</span>
              </label>
              {canEdit && heatmapMode !== 'prioritization' && (
                <button
                  onClick={() => setReorderMode(true)}
                  className="px-4 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50"
                >
                  Reorder & Group
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Heatmap Legend */}
      {heatmapMode !== 'none' && heatmapMode !== 'prioritization' && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium">Heatmap Legend</h3>
            <button
              onClick={() => setLegendExpanded(!legendExpanded)}
              className="sm:hidden p-1 text-gray-600 hover:text-gray-800"
              aria-label={legendExpanded ? 'Collapse legend' : 'Expand legend'}
            >
              <svg
                className={`w-5 h-5 transform transition-transform ${legendExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
          <div
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm ${legendExpanded ? 'block' : 'hidden sm:grid'}`}
          >
            <div>
              <div className="font-medium mb-1">Background Color</div>
              {heatmapMode === 'obviousness' ? (
                <>
                  <div className="text-gray-600 mb-1">
                    Progressive green intensity by score
                  </div>
                  <div className="flex items-center gap-1">
                    <div
                      className="w-4 h-4 border"
                      style={{ backgroundColor: 'rgb(255, 255, 255)' }}
                    ></div>
                    <span className="text-xs">0.0</span>
                    <div
                      className="w-4 h-4"
                      style={{ backgroundColor: 'rgb(240, 253, 244)' }}
                    ></div>
                    <span className="text-xs">3.0</span>
                    <div
                      className="w-4 h-4"
                      style={{ backgroundColor: 'rgb(134, 239, 172)' }}
                    ></div>
                    <span className="text-xs">7.0</span>
                    <div
                      className="w-4 h-4"
                      style={{ backgroundColor: 'rgb(34, 197, 94)' }}
                    ></div>
                    <span className="text-xs">10.0</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-gray-600 mb-1">
                    Progressive amber intensity by penetration
                  </div>
                  <div className="flex items-center gap-1">
                    <div
                      className="w-4 h-4 border"
                      style={{ backgroundColor: 'rgb(255, 255, 255)' }}
                    ></div>
                    <span className="text-xs">0%</span>
                    <div
                      className="w-4 h-4"
                      style={{ backgroundColor: 'rgb(254, 251, 235)' }}
                    ></div>
                    <span className="text-xs">30%</span>
                    <div
                      className="w-4 h-4"
                      style={{ backgroundColor: 'rgb(251, 191, 36)' }}
                    ></div>
                    <span className="text-xs">70%</span>
                    <div
                      className="w-4 h-4"
                      style={{ backgroundColor: 'rgb(217, 119, 6)' }}
                    ></div>
                    <span className="text-xs">100%</span>
                  </div>
                </>
              )}
            </div>
            <div>
              <div className="font-medium mb-1">Border Thickness</div>
              <div className="text-gray-600 mb-1">SOM market size</div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-300 border border-gray-600"></div>
                <span className="text-xs">Small</span>
                <div className="w-4 h-4 bg-gray-300 border-2 border-gray-600"></div>
                <span className="text-xs">Medium</span>
                <div className="w-4 h-4 bg-gray-300 border-4 border-gray-600"></div>
                <span className="text-xs">Large</span>
              </div>
            </div>
            <div>
              <div className="font-medium mb-1">Progress Bar</div>
              <div className="text-gray-600 mb-1">Market penetration %</div>
              <div className="flex items-center gap-1">
                <div className="w-8 h-2 bg-gray-300 rounded-full overflow-hidden">
                  <div className="w-3/4 h-full bg-white"></div>
                </div>
                <span className="text-xs">% captured</span>
              </div>
            </div>
            <div>
              <div className="font-medium mb-1">Credibility Badges</div>
              <div className="text-gray-600 mb-1">Evidence strength</div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-amber-200 rounded-full flex items-center justify-center text-xs font-bold text-amber-800">
                  A
                </div>
                <span className="text-xs">Awards</span>
                <div className="w-4 h-4 bg-blue-200 rounded-full flex items-center justify-center text-xs font-bold text-blue-800">
                  C
                </div>
                <span className="text-xs">Cases</span>
                <div className="w-4 h-4 bg-emerald-200 rounded-full flex items-center justify-center text-xs font-bold text-emerald-800">
                  T
                </div>
                <span className="text-xs">Testimonials</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conditional rendering based on view mode */}
      {heatmapMode === 'prioritization' ? (
        <div className="space-y-6">
          {/* Prioritization Controls */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Position Prioritization
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Drag and drop to reorder positions, or use auto-prioritization
                  algorithms
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleAutoSort('best')}
                  className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                  disabled={!canEdit}
                >
                  <SparklesIcon className="h-4 w-4" />
                  Best Overall
                </button>
                <button
                  onClick={() => handleAutoSort('obvious')}
                  className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                  disabled={!canEdit}
                >
                  <ArrowsUpDownIcon className="h-4 w-4" />
                  Most Obvious
                </button>
                <button
                  onClick={() => handleAutoSort('som')}
                  className="flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
                  disabled={!canEdit}
                >
                  <ArrowsUpDownIcon className="h-4 w-4" />
                  Largest Market
                </button>
                {hasUnsavedChanges && (
                  <button
                    onClick={saveChanges}
                    className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium shadow-sm"
                  >
                    <CheckCircleIcon className="h-4 w-4" />
                    Save Changes
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Prioritized Positions List */}
          <div className="bg-white rounded-lg shadow p-6">
            {positions.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handlePrioritizationDragEnd}
              >
                <SortableContext
                  items={positions.map((p) => p.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {positions.map((position, index) => (
                      <div key={position.id} className="relative">
                        <div className="absolute left-0 top-4 w-8 text-sm text-gray-500 font-medium">
                          #{index + 1}
                        </div>
                        <div className="ml-10">
                          <SortablePositionItem
                            position={position}
                            onFocusModeChange={handleFocusModeChange}
                            currency={organization?.currency || 'USD'}
                            canEdit={canEdit}
                            isSelected={selectedPositionId === position.id}
                            onClick={() => setSelectedPositionId(position.id)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No positions to prioritize.</p>
                <p className="text-sm text-gray-400 mt-1">
                  Create some segment/proposition combinations first.
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Matrix with DnD Context */
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table
                className="w-full border-collapse"
                style={{ tableLayout: 'fixed' }}
              >
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 sm:px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] w-[200px] max-w-[250px] border-r border-b border-gray-200">
                      Segment
                    </th>
                    {reorderMode
                      ? // Grouped proposition headers with Ungrouped last
                        groupedPropositions.map(([groupName, props]) => (
                          <React.Fragment key={`prop-header-${groupName}`}>
                            <SortableContext
                              items={props.map((p: any) => p.id)}
                              strategy={horizontalListSortingStrategy}
                            >
                              {props.map((p: any) => (
                                <SortablePropositionHeader
                                  key={p.id}
                                  proposition={p}
                                  onGroupMenuOpen={handleGroupMenuOpen}
                                  reorderMode={reorderMode}
                                  canEdit={canEdit}
                                />
                              ))}
                            </SortableContext>
                          </React.Fragment>
                        ))
                      : flatPropositions.map((p: any) => (
                          <th
                            key={p.id}
                            className="px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-b border-gray-200"
                            style={{
                              width: '160px',
                              minWidth: '160px',
                              maxWidth: '160px',
                            }}
                          >
                            <div className="flex flex-col items-center justify-center">
                              <div className="flex items-center justify-center gap-1">
                                {p.group?.color && (
                                  <div
                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: p.group.color }}
                                  />
                                )}
                                <div
                                  className="font-semibold text-center break-words leading-tight"
                                  style={{ maxWidth: '150px' }}
                                  title={p.name}
                                >
                                  {p.name}
                                </div>
                              </div>
                              {(p.group_name || p.group?.name) && (
                                <div
                                  className="text-xs text-gray-400 font-normal mt-1 truncate max-w-[140px] mx-auto"
                                  title={p.group_name || p.group?.name}
                                >
                                  ({p.group_name || p.group?.name})
                                </div>
                              )}
                            </div>
                          </th>
                        ))}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {reorderMode
                    ? groupedSegments.map(([groupName, segs]) => (
                        <React.Fragment key={`seg-body-${groupName}`}>
                          <SortableContext
                            items={segs.map((s: any) => s.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {segs.map((seg: any, segIndex: number) => {
                              const currentGroup = groupName;
                              const prevSeg =
                                segIndex > 0 ? segs[segIndex - 1] : null;
                              const prevGroup = prevSeg ? groupName : null;
                              const showDivider = segIndex === 0; // always show header at start of each group, including Ungrouped

                              return (
                                <SortableSegmentRow
                                  key={seg.id}
                                  segment={seg}
                                  propositions={propositions}
                                  filteredMatrix={filteredMatrix}
                                  handleCellClick={handleCellClick}
                                  onGroupMenuOpen={handleGroupMenuOpen}
                                  showDivider={showDivider}
                                  currentGroup={currentGroup}
                                  reorderMode={reorderMode}
                                  _organization={organization}
                                  canEdit={canEdit}
                                  heatmapView={heatmapMode}
                                  matrix={matrix}
                                />
                              );
                            })}
                          </SortableContext>
                        </React.Fragment>
                      ))
                    : flatSegments.map((seg: any, segIndex: number) => {
                        const currentGroup =
                          seg.group_name || seg.group?.name || 'Ungrouped';
                        const prevSeg =
                          segIndex > 0 ? flatSegments[segIndex - 1] : null;
                        const prevGroup = prevSeg
                          ? prevSeg.group_name ||
                            prevSeg.group?.name ||
                            'Ungrouped'
                          : null;
                        const showDivider =
                          segIndex === 0 || currentGroup !== prevGroup;

                        return (
                          <React.Fragment key={seg.id}>
                            {showDivider && (
                              <tr>
                                <td
                                  colSpan={propositions.length + 1}
                                  className="py-2 px-6 bg-gray-100 border-t-2 border-blue-500"
                                >
                                  <span className="text-sm font-bold text-blue-700">
                                    {currentGroup}
                                  </span>
                                </td>
                              </tr>
                            )}
                            <tr className="hover:bg-gray-50">
                              <td className="px-2 sm:px-4 py-4 min-w-[120px] w-[200px] max-w-[250px] align-middle border-r border-b border-gray-200">
                                <div className="flex items-center">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      {seg.group?.color && (
                                        <div
                                          className="w-2 h-2 rounded-full flex-shrink-0"
                                          style={{
                                            backgroundColor: seg.group.color,
                                          }}
                                        />
                                      )}
                                      <div
                                        className="text-sm font-medium text-gray-900 break-words leading-tight line-clamp-2"
                                        title={seg.name}
                                      >
                                        {seg.name}
                                      </div>
                                    </div>
                                    {seg.group_name && (
                                      <div
                                        className="text-xs text-gray-500 mt-1 truncate ml-4"
                                        title={seg.group_name}
                                      >
                                        ({seg.group_name})
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              {propositions.map((p: any) => {
                                const cell = filteredMatrix.find(
                                  (row: any) =>
                                    row.segment_id === seg.id &&
                                    row.proposition_id === p.id,
                                );

                                return (
                                  <td
                                    key={p.id}
                                    className={`${heatmapMode !== 'none' ? 'p-0 w-[160px] max-w-[160px] h-[160px] relative' : 'px-3 py-4 text-center w-[160px] max-w-[160px] border-r border-b border-gray-200'} ${
                                      heatmapMode === 'none'
                                        ? 'cursor-pointer hover:bg-gray-100'
                                        : ''
                                    } align-middle`}
                                    onClick={() =>
                                      cell &&
                                      heatmapMode === 'none' &&
                                      handleCellClick(
                                        cell.positioning_id || cell.id,
                                      )
                                    }
                                  >
                                    {heatmapMode !== 'none' ? (
                                      <HeatmapCell
                                        cell={cell}
                                        matrix={matrix}
                                        organization={organization}
                                        handleCellClick={handleCellClick}
                                        mode={
                                          heatmapMode as
                                            | 'obviousness'
                                            | 'penetration'
                                        }
                                      />
                                    ) : (
                                      <>
                                        {cell ? (
                                          <div className="space-y-1">
                                            {/* Score */}
                                            <div
                                              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getScoreChipColor(calculatePositioningScore(cell, organization?.id))}`}
                                            >
                                              Score:{' '}
                                              {calculatePositioningScore(
                                                cell,
                                                organization?.id,
                                              ).toFixed(1)}
                                            </div>

                                            {/* Focus Mode */}
                                            <div
                                              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getFocusModeTailwindClasses(cell.focus_mode, true)}`}
                                            >
                                              {formatFocusMode(
                                                cell.focus_mode,
                                              ) ?? 'No Focus'}
                                            </div>

                                            {/* SOM Value */}
                                            {cell.som_value && (
                                              <div className="inline-flex px-2 py-1 text-xs font-medium rounded-full border bg-blue-100 text-blue-800 border-blue-300">
                                                SOM:{' '}
                                                {formatCurrency(
                                                  Number(cell.som_value),
                                                  organization?.currency ||
                                                    'USD',
                                                  {
                                                    compact: true,
                                                  },
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-sm text-gray-400">
                                            No Data
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          </React.Fragment>
                        );
                      })}
                </tbody>
              </table>
            </div>
          </div>
        </DndContext>
      )}

      {/* Group Menu Modal */}
      {groupMenu.anchorEl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80 max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-4">Set Group</h3>
            <select
              value={selectedGroupId || ''}
              onChange={(e) => setSelectedGroupId(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
              autoFocus
            >
              <option value="">No group</option>
              {groups
                .filter((g) =>
                  groupMenu.type === 'segment'
                    ? g.group_type === 'segment'
                    : g.group_type === 'proposition',
                )
                .map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
            </select>
            <div className="text-sm text-gray-600 mb-4">
              To create new groups, visit the{' '}
              {groupMenu.type === 'segment' ? 'Segments' : 'Propositions'} page.
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={handleGroupMenuClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGroupSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
